import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './SeasonStartProgressModal.module.css';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import { convertDateTime } from 'utils/time';
import useGroupLabels from 'hooks/useGroupLabels';

// Rollover duration varies a lot between environments and club sizes, so the
// bar makes no time claims: it rises to 75% over the first 30 seconds, eases
// on to 95%, then waits there until the server responds. Only a real server
// response moves it to 100%.
const progressAt = (seconds) => {
  if (seconds <= 30) return (seconds / 30) * 75;
  if (seconds <= 60) return 75 + ((seconds - 30) / 30) * 20;
  return 95;
};

const formatElapsed = (seconds) => {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${String(secs).padStart(2, '0')}s`;
};

const SeasonStartProgressModal = ({
  isOpen,
  status,
  seasonName,
  result,
  error,
  onClose,
}) => {
  // What a new season does differs for a club that groups by year level: see groupPhrases in
  // utils/groupLabel.js.
  const { phrases } = useGroupLabels();
  const [elapsed, setElapsed] = useState(0);

  const running = isOpen && status === 'running';

  useEffect(() => {
    if (!running) return undefined;
    setElapsed(0);
    const startedAt = Date.now();
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [running]);

  // Closing the tab does not cancel the server-side rollover; warn so the
  // admin doesn't lose sight of an operation that is still committing.
  useEffect(() => {
    if (!running) return undefined;
    const warnBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [running]);

  if (!isOpen) return null;

  const progressPercent = progressAt(elapsed);

  const summaryRows = result
    ? [
        result.previousSeasonName && {
          label: 'Previous season',
          value: result.previousSeasonName,
        },
        { label: 'New active season', value: result.newSeasonName || seasonName },
        // A club that groups by year level has no age calculation: its students move up a year level.
        phrases.hasAgeCalculationDate && result.ageCalculationDate && {
          label: 'Age calculation date',
          value: convertDateTime(result.ageCalculationDate).date,
        },
        {
          label: 'Athletes processed',
          value: `${result.totalAthletesProcessed ?? 0}`,
        },
        {
          label: phrases.seasonUpdated,
          value: `${result.athletesWithAgeChanges ?? 0}`,
        },
      ].filter(Boolean)
    : [];

  return (
    // No overlay onClick — dialogs must not close on an outside click.
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {status === 'running' && 'Starting New Season'}
            {status === 'success' && 'New Season Started'}
            {status === 'error' && 'Season Start Failed'}
          </h2>
          {status !== 'running' && (
            <button className={styles.closeBtn} onClick={onClose}>
              &times;
            </button>
          )}
        </div>

        <div className={styles.modalBody}>
          {status === 'running' && (
            <>
              <div className={styles.modalDescription}>
                Activating <strong>{seasonName}</strong>. The server is
                {phrases.seasonUpdating}, archiving the outgoing season and
                clearing chat history — how long this takes varies with club
                size.
              </div>

              <div className={styles.progressTrack}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>

              <div className={styles.elapsedText}>
                <span className={styles.stageSpinner} />
                Working — elapsed {formatElapsed(elapsed)}
              </div>

              <div className={styles.keepOpenNotice}>
                The rollover runs on the server and cannot be cancelled. Keep
                this page open until the rollover is complete.
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className={styles.successBanner}>
                <span className={styles.successIcon}>✓</span>
                <span>
                  <strong>{result?.newSeasonName || seasonName}</strong> is now
                  the active season.
                </span>
              </div>

              {summaryRows.length > 0 && (
                <div className={styles.summary}>
                  {summaryRows.map((row) => (
                    <div key={row.label} className={styles.summaryItem}>
                      <span className={styles.summaryLabel}>{row.label}</span>
                      <span className={styles.summaryValue}>{row.value}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.modalActions}>
                <CustomNewButton
                  label="Close"
                  variant="success"
                  onClick={onClose}
                />
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className={styles.errorBanner}>{error}</div>
              <div className={styles.modalDescription}>
                No changes were made — the season rollover runs as a single
                all-or-nothing operation. Resolve the issue above and try
                again.
              </div>
              <div className={styles.modalActions}>
                <CustomNewButton
                  label="Close"
                  variant="secondary"
                  onClick={onClose}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

SeasonStartProgressModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  status: PropTypes.oneOf(['idle', 'running', 'success', 'error']).isRequired,
  seasonName: PropTypes.string,
  result: PropTypes.shape({
    newSeasonName: PropTypes.string,
    previousSeasonName: PropTypes.string,
    ageCalculationDate: PropTypes.string,
    totalAthletesProcessed: PropTypes.number,
    athletesWithAgeChanges: PropTypes.number,
  }),
  error: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default SeasonStartProgressModal;
